/**
 * VirtualKeyboard - Clavier virtuel tactile AZERTY
 *
 * Clavier flottant qui s'affiche automatiquement près du champ actif.
 * Optimisé pour écrans tactiles (Viewsonic 1655, tablettes).
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useVirtualKeyboardOptional } from '../../contexts/VirtualKeyboardContext';
import { placerClavier, planClavier, type Point, type Rect } from '../../utils/placementClavier';

/**
 * La fenêtre qui contient le champ : sous le fond `position: fixed` qui couvre
 * l'écran (le motif de toutes les fenêtres de la caisse — poids, paiement), le
 * plus grand ancêtre qui ne couvre PAS l'écran. C'est le panneau visible, et non
 * l'enveloppe de centrage pleine hauteur que certaines fenêtres interposent (la
 * déplacer ne déplacerait rien d'utile). Null si le champ n'est pas dans une fenêtre.
 */
function boiteDeDialogue(el: Element | null): HTMLElement | null {
  // Page plein écran déclarée comme telle : elle se resserre à côté du clavier.
  if (el?.closest('[data-clavier="page"]')) return null;
  const plein = (r: DOMRect) => r.width >= window.innerWidth * 0.9 && r.height >= window.innerHeight * 0.9;
  let n: Element | null = el;
  let boite: Element | null = null;
  while (n && n.parentElement && n.parentElement !== document.body) {
    const parent: Element = n.parentElement;
    if (!plein(n.getBoundingClientRect())) boite = n;
    if (getComputedStyle(parent).position === 'fixed' && plein(parent.getBoundingClientRect())) {
      return (boite ?? n) as HTMLElement;
    }
    n = parent;
  }
  return null;
}

/**
 * Ajoute un décalage à la translation propre de la fenêtre (propriété CSS
 * `translate`, celle qu'utilisent les utilitaires Tailwind v4) sans l'écraser.
 * Null si la valeur en place est trop complexe pour être composée sans risque.
 */
function composerTranslation(actuelle: string, d: Point): string | null {
  if (!actuelle || actuelle === 'none') return `${d.x}px ${d.y}px`;
  const parts = actuelle.trim().split(/\s+/);
  if (parts.length > 3 || parts.some((v) => !/^-?[\d.]+(px|%)$/.test(v))) return null;
  const [x, y = '0px', z] = parts;
  return `calc(${x} + ${d.x}px) calc(${y} + ${d.y}px)${z ? ` ${z}` : ''}`;
}

function rectDe(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

// Layout AZERTY français — tout sur un seul écran
const AZERTY_LAYOUT = {
  numbers: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ],
  letters: [
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['SHIFT', 'w', 'x', 'c', 'v', 'b', 'n', '.', '€', 'BACKSPACE'],
  ],
};

// Layout numérique (pour tel, number)
const NUMERIC_LAYOUT = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '€'],
];

export default function VirtualKeyboard() {
  const context = useVirtualKeyboardOptional();
  const [isShift, setIsShift] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [localPosition, setLocalPosition] = useState({ x: 0, y: 0 });
  const localPositionRef = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef<HTMLDivElement>(null);

  // Mode compact (taille divisée par 2) — utile dans les modals où le grand
  // clavier recouvre toute la fenêtre. Persisté en localStorage.
  const [isCompact, setIsCompact] = useState<boolean>(() => {
    try {
      return localStorage.getItem('virtualKeyboardCompact') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCompact = () => {
    setIsCompact((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('virtualKeyboardCompact', String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  // Garder le ref à jour avec la dernière position (pour le handler de fin de drag)
  useEffect(() => {
    localPositionRef.current = localPosition;
  }, [localPosition]);

  // Extraire les valeurs du contexte (avec valeurs par défaut si null)
  const isOpen = context?.isOpen ?? false;
  const position = context?.position ?? { x: 0, y: 0 };
  const inputType = context?.inputType ?? 'text';
  const handleKey = context?.handleKey ?? (() => {});
  const closeKeyboard = context?.closeKeyboard ?? (() => {});

  // Synchroniser la position locale uniquement à l'ouverture du clavier.
  // Si on resyncait à chaque changement de `position`, un refocus de champ
  // pendant le drag réinitialiserait la position et le clavier semblerait
  // « sauter » de manière incohérente.
  //
  // La position n'est plus prise telle quelle : elle est d'abord confrontée au
  // champ en saisie. Le contexte propose un emplacement (le coin bas-droit, ou
  // celui où le caissier a laissé le clavier) sans rien savoir du champ ; posé
  // ainsi, le pavé numérique recouvrait le poids libre et le montant du
  // paiement, qu'on tape au milieu d'une fenêtre centrée (signalé le
  // 25/09/2026). `useLayoutEffect` : on mesure la taille RÉELLE du clavier (pavé
  // ou AZERTY, réduit ou non) et on le place avant qu'il ne s'affiche — pas de
  // saut visible. On replace aussi quand le champ change ou que la taille change
  // (mode compact), mais seulement si la position courante cache quelque chose :
  // un caissier qui vient de déplacer le clavier ne le voit pas repartir.
  const wasOpenRef = useRef(false);
  const inputEl = context?.inputRef ?? null;

  // Ce que le plan a déplacé pour faire de la place au clavier (21/10/2026 —
  // « tout visible lorsque le pavé texte s'ouvre ») : la fenêtre glissée de côté,
  // ou la page resserrée. Tout est remis en place à la fermeture.
  const deplaceRef = useRef<{ boite: HTMLElement; translate: string } | null>(null);
  const reserveRef = useRef(false);
  const remettreEnPlace = () => {
    const d = deplaceRef.current;
    if (d) {
      d.boite.style.translate = d.translate;
      deplaceRef.current = null;
    }
    if (reserveRef.current) {
      const html = document.documentElement;
      html.classList.remove('clavier-a-cote');
      html.style.removeProperty('--clavier-reserve-gauche');
      html.style.removeProperty('--clavier-reserve-droite');
      reserveRef.current = false;
    }
  };
  useEffect(() => remettreEnPlace, []);

  useLayoutEffect(() => {
    const vientDOuvrir = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (isDragging) return;
    // Toujours repartir de la mise en page d'origine : mesures justes, et rien
    // ne reste décalé quand le clavier se ferme ou passe à un autre champ.
    remettreEnPlace();
    if (!isOpen) return;
    const kb = keyboardRef.current;
    if (!kb) {
      if (vientDOuvrir) setLocalPosition(position);
      return;
    }
    const r = kb.getBoundingClientRect();
    const taille = { w: r.width, h: r.height };
    const ecran = { w: window.innerWidth, h: window.innerHeight };
    const depart = vientDOuvrir ? position : localPositionRef.current;
    let cible: Point;
    if (!inputEl) {
      cible = placerClavier([], taille, ecran, depart);
    } else {
      const boite = boiteDeDialogue(inputEl);
      const plan = planClavier({
        champ: rectDe(inputEl),
        boite: boite ? rectDe(boite) : null,
        vente: !!inputEl.closest('[data-tactile="vente"]'),
        clavier: taille,
        ecran,
        souhait: depart,
      });
      cible = plan.clavier;
      if (boite && (plan.decalage.x || plan.decalage.y)) {
        const translation = composerTranslation(getComputedStyle(boite).translate, plan.decalage);
        if (translation) {
          deplaceRef.current = { boite, translate: boite.style.translate };
          boite.style.translate = translation;
        }
      }
      if (plan.reserve) {
        const html = document.documentElement;
        html.style.setProperty(
          plan.reserve.cote === 'droite' ? '--clavier-reserve-droite' : '--clavier-reserve-gauche',
          `${plan.reserve.largeur}px`,
        );
        html.classList.add('clavier-a-cote');
        reserveRef.current = true;
      }
      // La page vient de se resserrer, ou la fenêtre défile : le champ revient sous les yeux.
      if (plan.reserve || boite) inputEl.scrollIntoView({ block: plan.reserve ? 'center' : 'nearest', inline: 'nearest' });
    }
    if (cible.x !== localPositionRef.current.x || cible.y !== localPositionRef.current.y || vientDOuvrir) {
      localPositionRef.current = cible;
      setLocalPosition(cible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inputEl, inputType, isCompact]);

  // inputType est utilisé pour le rendu numérique simplifié (tel/number)

  // Gestion du drag (souris + tactile).
  //
  // Pendant le drag, on manipule directement le DOM (style.left/top) via
  // keyboardRef au lieu de passer par setLocalPosition. Le batching de
  // React peut sinon décaler le re-render et donner l'impression que le
  // clavier ne bouge pas pendant le déplacement (visible seulement à la
  // prochaine ouverture). On ne synchronise le state React qu'à la fin
  // du drag pour rester cohérent avec le reste du composant.
  useEffect(() => {
    const moveTo = (clientX: number, clientY: number) => {
      const newX = clientX - dragOffset.x;
      const newY = clientY - dragOffset.y;
      // Bornes basées sur la taille VISUELLE du clavier (getBoundingClientRect
      // respecte les transforms — important quand isCompact applique scale(0.5)
      // alors qu'offsetWidth ignorerait l'échelle).
      const el = keyboardRef.current;
      const rect = el?.getBoundingClientRect();
      const kbWidth = rect?.width ?? 1200;
      const kbHeight = rect?.height ?? 680;
      const maxX = window.innerWidth - kbWidth;
      const maxY = window.innerHeight - kbHeight;
      const pos = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      };
      // Ref toujours à jour pour le handler de fin de drag.
      localPositionRef.current = pos;
      // Mise à jour DOM directe — instantané, pas de re-render.
      if (el) {
        el.style.left = `${pos.x}px`;
        el.style.top = `${pos.y}px`;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) moveTo(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches[0]) {
        e.preventDefault(); // empêche le scroll de la page pendant le drag
        moveTo(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      // Synchroniser le state React avec la position finale du DOM.
      setLocalPosition(localPositionRef.current);
      try {
        localStorage.setItem(
          'virtualKeyboardPosition',
          JSON.stringify(localPositionRef.current),
        );
      } catch {
        // ignore storage errors
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      window.addEventListener('touchcancel', handleEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleEnd);
      window.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDragging, dragOffset]);

  // Si pas de contexte ou clavier fermé, ne rien afficher
  if (!context || !isOpen) {
    return null;
  }

  const handleKeyPress = (key: string) => {
    if (key === 'SHIFT') {
      setIsShift(!isShift);
      return;
    }

    if (key === 'BACKSPACE') {
      handleKey('BACKSPACE');
      return;
    }

    // Appliquer le shift si actif
    const finalKey = isShift ? key.toUpperCase() : key;
    handleKey(finalKey);

    // Désactiver le shift après une touche (comme un vrai clavier)
    if (isShift) {
      setIsShift(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Ne permettre le drag que sur la barre de titre
    if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - localPosition.x,
        y: e.clientY - localPosition.y,
      });
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!e.touches[0]) return;
    if ((e.target as HTMLElement).closest('[data-drag-handle]')) {
      setIsDragging(true);
      setDragOffset({
        x: e.touches[0].clientX - localPosition.x,
        y: e.touches[0].clientY - localPosition.y,
      });
    }
  };

  // Rendu pour mode numérique simplifié
  if (inputType === 'tel' || inputType === 'number') {
    return (
      <div
        ref={keyboardRef}
        data-virtual-keyboard
        className="fixed z-[10000] select-none"
        style={{
          left: `${localPosition.x}px`,
          top: `${localPosition.y}px`,
          touchAction: 'none',
          transform: isCompact ? 'scale(0.5)' : undefined,
          transformOrigin: 'top left',
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="bg-white rounded-xl shadow-2xl border-2 border-gray-300 overflow-hidden w-[560px]">
          {/* Header */}
          <div
            data-drag-handle
            className="bg-gradient-to-r from-gray-700 to-gray-800 px-8 py-4 flex items-center justify-between cursor-move active:cursor-grabbing"
          >
            <span className="text-white text-2xl font-medium select-none">⋮⋮ Clavier numérique (déplaçable)</span>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleCompact}
                title={isCompact ? 'Agrandir' : 'Réduire'}
                className="w-14 h-14 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full text-white font-bold text-2xl"
              >
                {isCompact ? '⤢' : '⤡'}
              </button>
              <button
                onClick={closeKeyboard}
                className="w-14 h-14 flex items-center justify-center bg-red-500 hover:bg-red-600 rounded-full text-white font-bold text-2xl"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Numeric Pad */}
          <div className="p-6 bg-gray-100">
            {NUMERIC_LAYOUT.map((row, rowIndex) => (
              <div key={rowIndex} className="flex gap-4 mb-4 last:mb-0 justify-center">
                {row.map((key) => (
                  <KeyButton
                    key={key}
                    label={key}
                    onClick={() => handleKeyPress(key)}
                    className="w-32 h-28"
                  />
                ))}
              </div>
            ))}

            {/* Action Row */}
            <div className="flex gap-4 mt-4">
              <KeyButton
                label="⌫"
                onClick={() => handleKey('BACKSPACE')}
                className="w-32 h-24 !bg-orange-500 hover:!bg-orange-600 text-white"
              />
              <KeyButton
                label="Effacer"
                onClick={() => handleKey('CLEAR')}
                className="flex-1 h-24 !bg-red-100 hover:!bg-red-200 text-red-700 text-xl font-medium"
              />
              <KeyButton
                label="↵"
                onClick={() => handleKey('ENTER')}
                className="w-32 h-24 !bg-green-500 hover:!bg-green-600 text-white"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rendu pour mode texte complet (AZERTY) — tout sur un écran
  return (
    <div
      ref={keyboardRef}
      data-virtual-keyboard
      className="fixed z-[10000] select-none"
      style={{
        left: `${localPosition.x}px`,
        top: `${localPosition.y}px`,
        touchAction: 'none',
        transform: isCompact ? 'scale(0.5)' : undefined,
        transformOrigin: 'top left',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="bg-white rounded-xl shadow-2xl border-2 border-gray-300 overflow-hidden w-[1200px]">
        {/* Header */}
        <div
          data-drag-handle
          className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-4 flex items-center justify-between cursor-move active:cursor-grabbing"
        >
          <span className="text-white text-2xl font-medium select-none">⋮⋮ Clavier virtuel (déplaçable)</span>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleCompact}
              title={isCompact ? 'Agrandir' : 'Réduire'}
              className="w-14 h-14 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full text-white font-bold text-2xl"
            >
              {isCompact ? '⤢' : '⤡'}
            </button>
            <button
              onClick={closeKeyboard}
              className="w-14 h-14 flex items-center justify-center bg-red-500 hover:bg-red-600 rounded-full text-white font-bold text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Keyboard Body */}
        <div className="p-6 bg-gray-100">
          {/* Numbers row */}
          <div className="flex gap-2 mb-4 justify-center">
            {AZERTY_LAYOUT.numbers[0].map((key) => (
              <KeyButton
                key={key}
                label={key}
                onClick={() => handleKeyPress(key)}
                className="w-[104px] h-[88px]"
              />
            ))}
          </div>

          {/* Letter rows */}
          {AZERTY_LAYOUT.letters.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-2 mb-4 justify-center">
              {row.map((key) => {
                if (key === 'SHIFT') {
                  return (
                    <KeyButton
                      key={key}
                      label="⇧"
                      onClick={() => handleKeyPress(key)}
                      className={`w-32 h-[88px] ${isShift ? '!bg-blue-500 text-white' : '!bg-gray-300'}`}
                    />
                  );
                }
                if (key === 'BACKSPACE') {
                  return (
                    <KeyButton
                      key={key}
                      label="⌫"
                      onClick={() => handleKeyPress(key)}
                      className="w-32 h-[88px] !bg-orange-500 hover:!bg-orange-600 text-white"
                    />
                  );
                }
                return (
                  <KeyButton
                    key={key}
                    label={isShift ? key.toUpperCase() : key}
                    onClick={() => handleKeyPress(key)}
                    className="w-[104px] h-[88px]"
                  />
                );
              })}
            </div>
          ))}

          {/* Bottom row: Space + Effacer + Enter */}
          <div className="flex gap-4 mt-4">
            <KeyButton
              label="Espace"
              onClick={() => handleKey('SPACE')}
              className="flex-1 h-[88px] !bg-gray-200 hover:!bg-gray-300"
            />
            <KeyButton
              label="Effacer"
              onClick={() => handleKey('CLEAR')}
              className="w-40 h-[88px] !bg-red-100 hover:!bg-red-200 text-red-700 text-xl font-medium"
            />
            <KeyButton
              label="↵"
              onClick={() => handleKey('ENTER')}
              className="w-32 h-[88px] !bg-green-500 hover:!bg-green-600 text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant bouton de touche
interface KeyButtonProps {
  label: string;
  onClick: () => void;
  className?: string;
}

function KeyButton({ label, onClick, className = '' }: KeyButtonProps) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      onMouseDown={(e) => e.preventDefault()} // Empêcher le focus shift
      className={`
        rounded-lg font-medium text-4xl
        bg-white hover:bg-gray-100 active:bg-gray-200
        border border-gray-300 shadow-sm
        transition-all duration-75
        active:scale-95
        ${className}
      `}
    >
      {label}
    </button>
  );
}
